import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SavedSequenceDiagram } from '../../model/diagram/sequenceDiagram';
import { AuthApiService } from '../auth/auth-api.service';
import { AuthenticatedUser } from '../../model/user/authenticated-user.type';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class DiagramApiService {
  private readonly _baseUrl = environment.apiUrl;
  private readonly _http = inject(HttpClient);
  private readonly _authApiService = inject(AuthApiService);
  private readonly _toastr = inject(ToastrService);

  postDiagram(sequenceDiagramContent: string, sequenceDiagramTitle: string): Observable<SavedSequenceDiagram> {
    return this._authApiService.getUser$().pipe(
      switchMap((user: AuthenticatedUser | null) => {
        if (!user) {
          return throwError(() => new Error("L'utilisateur n'est pas authentifié."));
        }
        return this._http.post<SavedSequenceDiagram>(`${this._baseUrl}/user/${user.id}/sequence_diagrams`, {
          sequenceDiagramContent,
          sequenceDiagramTitle,
        });
      }),
      catchError(() => {
        return throwError(() => new Error('Erreur lors de la création du diagramme.'));
      })
    );
  }

  updateDiagram(diagramId: number, sequenceDiagramContent: string, sequenceDiagramTitle: string): Observable<void> {
    return this._authApiService.getUser$().pipe(
      switchMap((user: AuthenticatedUser | null) => {
        if (!user) {
          this._toastr.error("Impossible de mettre à jour le diagramme. L'utilisateur n'est pas authentifié.", 'Erreur de connexion');
          return of();
        }
        return this._http
          .put<void>(`${this._baseUrl}/user/${user.id}/sequence_diagrams/${diagramId}`, {
            sequenceDiagramContent,
            sequenceDiagramTitle,
          })
          .pipe(
            catchError(() => {
              this._toastr.error('Erreur lors de la mise à jour du diagramme.', 'Erreur');
              return of();
            })
          );
      })
    );
  }

  public getDiagramById(id: number): Observable<SavedSequenceDiagram | null> {
    return this._authApiService.getUser$().pipe(
      switchMap((user: AuthenticatedUser | null) => {
        if (!user) return of(null);
        return this._http.get<SavedSequenceDiagram>(`${this._baseUrl}/user/${user.id}/sequence_diagrams/${id}`);
      })
    );
  }

  public getAllDiagrams(): Observable<SavedSequenceDiagram[]> {
    return this._authApiService.getUser$().pipe(
      switchMap((user: AuthenticatedUser | null) => {
        if (!user) return of([]);
        return this._http.get<SavedSequenceDiagram[]>(`${this._baseUrl}/user/${user.id}/sequence_diagrams`);
      })
    );
  }

  public deleteDiagram(id: number): Observable<void> {
    return this._authApiService.getUser$().pipe(
      switchMap((user: AuthenticatedUser | null) => {
        if (!user) return of();
        return this._http.delete<void>(`${this._baseUrl}/user/${user.id}/sequence_diagrams/${id}`);
      })
    );
  }
}
